import React, { useEffect, useMemo, useState } from 'react';
import './CariHareketDateFilter.css';

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDisplayDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
};

const PRESETS = [
  {
    id: 'this-month',
    label: 'Bu ay',
    getRange: () => {
      const now = new Date();
      return {
        baslangicTarihi: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        bitisTarihi: toIsoDate(now),
      };
    },
  },
  {
    id: 'last-30',
    label: 'Son 30 gün',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { baslangicTarihi: toIsoDate(start), bitisTarihi: toIsoDate(now) };
    },
  },
  {
    id: 'last-3m',
    label: 'Son 3 ay',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      return { baslangicTarihi: toIsoDate(start), bitisTarihi: toIsoDate(now) };
    },
  },
];

const CariHareketDateFilter = ({ value = {}, onChange, disabled = false }) => {
  const [draft, setDraft] = useState({
    baslangicTarihi: value.baslangicTarihi || '',
    bitisTarihi: value.bitisTarihi || '',
  });

  useEffect(() => {
    setDraft({
      baslangicTarihi: value.baslangicTarihi || '',
      bitisTarihi: value.bitisTarihi || '',
    });
  }, [value.baslangicTarihi, value.bitisTarihi]);

  const hasAppliedFilter = Boolean(value.baslangicTarihi || value.bitisTarihi);
  const hasDraftChanges =
    draft.baslangicTarihi !== (value.baslangicTarihi || '') ||
    draft.bitisTarihi !== (value.bitisTarihi || '');

  const activeLabel = useMemo(() => {
    if (!hasAppliedFilter) return null;
    const start = formatDisplayDate(value.baslangicTarihi) || '…';
    const end = formatDisplayDate(value.bitisTarihi) || '…';
    return `${start} – ${end}`;
  }, [hasAppliedFilter, value.baslangicTarihi, value.bitisTarihi]);

  const applyRange = (range) => {
    if (range.baslangicTarihi && range.bitisTarihi && range.baslangicTarihi > range.bitisTarihi) {
      window.showNotification?.('error', 'Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }
    onChange({
      baslangicTarihi: range.baslangicTarihi || undefined,
      bitisTarihi: range.bitisTarihi || undefined,
    });
  };

  const handleApply = () => {
    applyRange(draft);
  };

  const handleClear = () => {
    setDraft({ baslangicTarihi: '', bitisTarihi: '' });
    onChange({});
  };

  const handlePreset = (preset) => {
    const range = preset.getRange();
    setDraft(range);
    applyRange(range);
  };

  return (
    <div className={`cari-date-filter ${hasAppliedFilter ? 'is-active' : ''}`}>
      <div className="cari-date-filter__header">
        <div className="cari-date-filter__title-wrap">
          <span className="cari-date-filter__icon" aria-hidden="true">
            <i className="fas fa-calendar-alt" />
          </span>
          <div>
            <div className="cari-date-filter__title">Tarih aralığı</div>
            <div className="cari-date-filter__subtitle">
              {hasAppliedFilter ? 'Filtre uygulanıyor' : 'Hareketleri tarihe göre süzün'}
            </div>
          </div>
        </div>
        {activeLabel ? (
          <span className="cari-date-filter__badge">{activeLabel}</span>
        ) : null}
      </div>

      <div className="cari-date-filter__row">
        <label className="cari-date-filter__field">
          <span>Başlangıç</span>
          <input
            type="date"
            value={draft.baslangicTarihi}
            onChange={(e) => setDraft((prev) => ({ ...prev, baslangicTarihi: e.target.value }))}
            disabled={disabled}
          />
        </label>
        <span className="cari-date-filter__sep" aria-hidden="true">—</span>
        <label className="cari-date-filter__field">
          <span>Bitiş</span>
          <input
            type="date"
            value={draft.bitisTarihi}
            onChange={(e) => setDraft((prev) => ({ ...prev, bitisTarihi: e.target.value }))}
            disabled={disabled}
          />
        </label>
        <div className="cari-date-filter__actions">
          <button
            type="button"
            className="primary-btn cari-date-filter__apply"
            onClick={handleApply}
            disabled={disabled || (!draft.baslangicTarihi && !draft.bitisTarihi && !hasAppliedFilter)}>
            Uygula
          </button>
          {(hasAppliedFilter || draft.baslangicTarihi || draft.bitisTarihi) && (
            <button
              type="button"
              className="secondary-btn"
              onClick={handleClear}
              disabled={disabled}>
              Temizle
            </button>
          )}
        </div>
      </div>

      <div className="cari-date-filter__presets">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="cari-date-filter__chip"
            onClick={() => handlePreset(preset)}
            disabled={disabled}>
            {preset.label}
          </button>
        ))}
        {hasDraftChanges && !hasAppliedFilter ? (
          <span className="cari-date-filter__draft-hint">Değişiklikler henüz uygulanmadı</span>
        ) : null}
      </div>
    </div>
  );
};

export default CariHareketDateFilter;
